import * as React from 'react';
import isNil from 'lodash/isNil';

import MetricsBar from 'components/metrics-bar/MetricsBar';
import Spinner from 'components/spinner/Spinner';

import { __t, applyTranslations } from 'utils/language';
import { insertIf } from 'utils/list';
import { renderPrice } from 'utils/helper/price';
import { getAbbreviatedMetricValue } from '../../utils/onsite-performance';

import type { MetricsBarItem } from 'types';
import type { OnsitePerformanceSummary as OnsitePerformanceSummaryAlias } from '../../types/OnsitePerformance.type';

import './onsite-performance-summary.scss';

applyTranslations(require('./onsite-performance-summary.i18n.json'));

const prepareOnsitePerformanceMetrics = (summary: OnsitePerformanceSummaryAlias): Array<MetricsBarItem> => [
  {
    key: 'creators',
    value: summary.creatorsCount,
    name: __t('onsite_performance_summary.content_creator_label', { withPlural: summary.creatorsCount })
  },
  ...insertIf(summary.taskCounts.instagramPost, {
    key: 'ig-posts',
    value: summary.taskCounts.instagramPost,
    name: __t('onsite_performance_summary.ig_posts_label', { withPlural: summary.taskCounts.instagramPost }),
    popover: __t('onsite_performance_summary.ig_posts_popover')
  }),
  ...insertIf(summary.taskCounts.instagramStory, {
    key: 'ig-stories',
    value: summary.taskCounts.instagramStory,
    name: __t('onsite_performance_summary.ig_stories_label', { withPlural: summary.taskCounts.instagramStory }),
    popover: __t('onsite_performance_summary.ig_stories_popover')
  }),
  ...insertIf(summary.taskCounts.instagramReel, {
    key: 'ig-reels',
    value: summary.taskCounts.instagramReel,
    name: __t('onsite_performance_summary.ig_reels_label', { withPlural: summary.taskCounts.instagramReel }),
    popover: __t('onsite_performance_summary.ig_reels_popover')
  }),
  ...insertIf(summary.taskCounts.tiktokVideo, {
    key: 'tt-videos',
    value: summary.taskCounts.tiktokVideo,
    name: __t('onsite_performance_summary.tt_videos_label', { withPlural: summary.taskCounts.tiktokVideo }),
    popover: __t('onsite_performance_summary.tt_videos_popover')
  }),
  {
    key: 'link-clicks',
    value: getAbbreviatedMetricValue(summary.onsiteMetrics.clicks),
    name: __t('onsite_performance_summary.link_clicks_label'),
    popover: __t('onsite_performance_summary.link_clicks_popover')
  },
  {
    key: 'pdp-views',
    value: getAbbreviatedMetricValue(summary.onsiteMetrics.pdpViews),
    name: __t('onsite_performance_summary.pdp_views_label'),
    popover: __t('onsite_performance_summary.pdp_views_popover')
  },
  {
    key: 'wishlist-items',
    value: getAbbreviatedMetricValue(summary.onsiteMetrics.itemsWishlist),
    name: __t('onsite_performance_summary.wishlist_items_label'),
    popover: __t('onsite_performance_summary.wishlist_items_popover')
  },
  {
    key: 'basket-items',
    value: getAbbreviatedMetricValue(summary.onsiteMetrics.itemsBasket),
    name: __t('onsite_performance_summary.basket_items_label'),
    popover: __t('onsite_performance_summary.basket_items_popover')
  },
  {
    key: 'sold-items',
    value: getAbbreviatedMetricValue(summary.onsiteMetrics.itemsSold),
    name: __t('onsite_performance_summary.sold_items_label'),
    popover: __t('onsite_performance_summary.sold_items_popover')
  },
  {
    key: 'gmv',
    value: isNil(summary.onsiteMetrics.gmv) ?
      'n/a' :
      renderPrice(summary.onsiteMetrics.gmv, { abbreviate: { decimal: 1, allowDecimalPlaces: true } }),
    name: __t('onsite_performance_summary.gmv_label'),
    popover: __t('onsite_performance_summary.gmv_popover')
  }
];

type Props = {
  hasData: boolean,
  isLoading: boolean,
  summary: OnsitePerformanceSummaryAlias
};

const OnsitePerformanceSummary = ({ hasData, isLoading, summary }: Props): JSX.Element | null => {
  if (!isLoading && !hasData) {
    return null;
  }

  return (
    <section className="onsite-performance-summary">
      {
        isLoading && (
          <div className="onsite-performance-summary__spinner">
            <Spinner
              position="inline"
              size="small"
              color="light"
            />
          </div>
        )
      }
      {
        !isLoading && (
          <MetricsBar
            isMonochrome
            padding="small"
            classNamePopover="onsite-performance-summary__popover"
            metrics={prepareOnsitePerformanceMetrics(summary)}
          />
        )
      }
    </section>
  );
};

export default OnsitePerformanceSummary;
